import { HardHat, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { SectionTitle } from '../Luxury';

interface RenovationPhase {
  label: string;
  sublabel: string;
  noi: number;
  debtService: number;
  cashFlow: number;
  occupancy: string;
  rentPerUnit: number;
  isConstructionYear: boolean;
}

interface RenovationWaterfallCardProps {
  phases: RenovationPhase[];
  totalUnits: number;
  capexBudget: number;
  className?: string;
}

function PhaseCard({ phase }: { phase: RenovationPhase }) {
  const cashFlowColor = phase.cashFlow >= 0 ? 'text-emerald' : 'text-crimson';
  const borderClass = phase.isConstructionYear
    ? 'border-dashed border-amber-500/50'
    : 'border-slate-dark';

  return (
    <div className={`bg-charcoal border ${borderClass} rounded-lg p-4 flex flex-col`}>
      {/* Header */}
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-ivory">{phase.label}</h4>
        <p className="text-sm text-stone-700">{phase.sublabel}</p>
      </div>

      {/* Occupancy Badge */}
      <div className="mb-3">
        <span className={`inline-block text-sm uppercase tracking-wider font-semibold px-2 py-1 rounded ${
          phase.isConstructionYear
            ? 'bg-amber-900/30 text-amber-400 border border-amber-600/30'
            : 'bg-emerald/10 text-emerald border border-emerald/30'
        }`}>
          {phase.occupancy}
        </span>
      </div>

      {/* Metrics */}
      <div className="space-y-2 text-sm flex-1">
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">Rent/Unit</span>
          <span className="font-mono tabular-nums text-ivory">
            {formatCurrency(phase.rentPerUnit)}
          </span>
        </div>

        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">NOI</span>
          <span className="font-mono tabular-nums text-ivory">
            {formatCurrency(phase.noi)}
          </span>
        </div>

        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">Debt Service</span>
          {phase.isConstructionYear && phase.debtService === 0 ? (
            <span className="text-sm text-stone-700 italic">
              No perm DS
            </span>
          ) : (
            <span className="font-mono tabular-nums text-crimson">
              ({formatCurrency(phase.debtService)})
            </span>
          )}
        </div>

        <div className="border-t border-gold/20 mt-1 pt-2 flex justify-between items-center">
          <span className="text-ivory font-semibold">Cash Flow</span>
          <span className={`font-mono tabular-nums font-bold text-lg ${cashFlowColor}`}>
            {formatCurrency(phase.cashFlow)}
          </span>
        </div>
      </div>

      {/* Construction year note */}
      {phase.isConstructionYear && phase.debtService === 0 && (
        <div className="mt-3 text-sm text-amber-400/70 leading-relaxed">
          No permanent debt service during construction
        </div>
      )}
    </div>
  );
}

export function RenovationWaterfallCard({
  phases,
  totalUnits,
  capexBudget,
  className = '',
}: RenovationWaterfallCardProps) {
  return (
    <div className={`bg-obsidian rounded-xl p-6 ${className}`}>
      <SectionTitle icon={<HardHat className="w-5 h-5" />}>
        Renovation Cash Flow Waterfall
      </SectionTitle>

      {/* Summary line */}
      <div className="mt-2 mb-5 flex items-center gap-4 text-sm text-stone-700">
        <span>
          {totalUnits} units
        </span>
        <span className="text-stone-700">|</span>
        <span>
          CapEx: <span className="font-mono tabular-nums text-ivory">{formatCurrency(capexBudget)}</span>
        </span>
      </div>

      {/* Phase Grid with Arrows */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
        {phases.map((phase, idx) => (
          <div key={idx} className="contents">
            <PhaseCard phase={phase} />
            {idx < phases.length - 1 && (
              <div className="hidden md:flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-gold/50" />
              </div>
            )}
          </div>
        ))}
      </div>

    </div>
  );
}
